<script>
import options from "./BasketAside.options";
export default options;
</script>

<template>
  <aside class="kb-basket-aside">
    <div class="kb-basket-aside kb-basket-aside__main-content-wrapper">
      <table class="kb-basket-aside__table" role="presentation">
        <thead class="kb-basket-aside__table-head">
          <tr>
            <th class="kb-basket-aside__table-head-title" colspan="1">
              {{ getLabel("asideTitle") }}
            </th>
            <th class="kb-basket-aside__table-head-link" colspan="1">
              <template v-if="!miniBasket">
                <a :href="getLabel('asideLinkUrl')">{{
                  getLabel("asideLinkText")
                }}</a>
              </template>
              <template v-if="miniBasket">
                <a :href="getLabel('toCartButtonLink')">{{
                  getLabel("toCartButtonLinkText")
                }}</a>
              </template>
            </th>
          </tr>
        </thead>

        <tbody class="kb-basket-aside__table-body">
          <tr>
            <td class="kb-basket-aside__table-body-data" colspan="2">
              <div class="kb-basket-aside__table-horizontal-line"></div>
            </td>
          </tr>

          <template
            v-if="miniBasket"
            v-for="(card, index) in basketCards"
            :key="index"
          >
            <tr>
              <td class="kb-basket-aside__table-body-data" colspan="2">
                <BasketCard
                  :miniBasket="miniBasket"
                  :data="card.data"
                  :variantData="card.variantData"
                  :soldOut="false"
                  :alreadyRemoved="false"
                  :errorMaxAmountMessage="card.errorMaxAmountMessage"
                />
              </td>
            </tr>
          </template>

          <template v-if="miniBasket">
            <tr>
              <td class="kb-basket-aside__table-body-data" colspan="2">
                <div
                  class="kb-basket-aside__table-horizontal-line kb-basket-aside__table-horizontal-line--bottom"
                ></div>
              </td>
            </tr>
          </template>

          <tr>
            <template v-if="!miniBasket">
              <td class="kb-basket-aside__table-body-data">
                {{ asideResults?.productsAmount }}
                {{ getLabel("productsAmountText") }}
              </td>
            </template>
            <template v-if="miniBasket">
              <td class="kb-basket-aside__table-body-data">
                {{ getLabel("totalTitle") }}
              </td>
            </template>
            <td class="kb-basket-aside__table-body-data">
              {{ formatAmount(asideResults?.productsPrice) }}
            </td>
          </tr>

          <tr>
            <td
              class="kb-basket-aside__table-body-data kb-basket-aside__table-body-data--delivery"
            >
              {{ getLabel("deliveryTitle") }}
              <TooltipComponent
                :tooltipText="getLabel('tooltipText', true)"
                :tooltipName="getLabel('tooltipText', true)"
              />
            </td>
            <td class="kb-basket-aside__table-body-data">
              {{
                asideResults?.isShippingFree ?? false
                  ? getLabel("deliveryText")
                  : formatAmount(asideResults?.shippingTotal)
              }}
            </td>
          </tr>

          <tr>
            <td
              class="kb-basket-aside__table-body-data kb-basket-aside__table-body-data--pale"
            >
              {{ getLabel("discountTitle") }}
            </td>
            <td
              class="kb-basket-aside__table-body-data kb-basket-aside__table-body-data--pale"
            >
              {{ formatAmount(asideResults?.discount) }}
            </td>
          </tr>

          <template v-if="!miniBasket">
            <tr>
              <td
                class="kb-basket-aside__table-body-data"
                colspan="2"
                :class="{
                  'kb-basket-aside__table-body-data--hide': !displayObsMessage,
                }"
              >
                <!-- class for error-message styles from product details 
                as they are identical & so future changes can be done in one place -->
                <p
                  class="kb-product-detail__error-message kb-product-detail__error-message--show"
                >
                  {{ getLabel("infoMessage") }}
                </p>
              </td>
            </tr>
          </template>

          <tr>
            <td class="kb-basket-aside__table-body-data" colspan="2">
              <div
                class="kb-basket-aside__table-horizontal-line kb-basket-aside__table-horizontal-line--bottom"
              ></div>
            </td>
          </tr>

          <tr class="kb-basket-aside__table-body--row-total">
            <td class="kb-basket-aside__table-body-data">
              {{ getLabel("totalTitle") }}
            </td>
            <td class="kb-basket-aside__table-body-data">
              {{ formatAmount(asideResults?.total) }}
            </td>
          </tr>

          <tr class="kb-basket-aside__table-body--row-small-fonts">
            <td
              class="kb-basket-aside__table-body-data kb-basket-aside__table-body-data--pale"
            >
              {{ getLabel("VATTitle") }}
            </td>
            <td
              class="kb-basket-aside__table-body-data kb-basket-aside__table-body-data--pale"
            >
              {{ formatAmount(asideResults?.VAT) }}
            </td>
          </tr>
        </tbody>
      </table>

      <template v-if="!miniBasket">
        <div class="kb-basket-aside__button-wrapper">
          <a
            :href="hasErrors ? '#' : getLabel('toCartButtonLink')"
            class="kb-button kb-button--full-color-spot"
          >
            {{ getLabel("toCartButtonLinkText") }}
          </a>
          <template v-if="getLabel('basketErrorsMessage', {allowEmpty:true})">
            <p
              class="kb-product-detail__error-message"
              :class="displayBasketErrorMessageClass"
            >
              {{ getLabel("basketErrorsMessage", {allowEmpty:true}) }}
            </p>
          </template>
        </div>
      </template>
    </div>

    <div class="kb-basket-aside__USP-wrapper-icons"
    v-if="showPaymentOptions">
      <svg
        preserveAspectRatio="xMidYMid meet"
        viewBox="0 0 60 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="MobilePay"
      >
        <rect x=".5" y=".5" width="59" height="39" rx="3.5" fill="#fff" />
        <rect x=".5" y=".5" width="59" height="39" rx="3.5" stroke="#E7E7E4" />
        <path
          d="M27.41 33a2.115 2.115 0 0 1-1.954-1.305l-7.295-17.61a2.112 2.112 0 0 1 1.143-2.76l7.64-3.164a2.111 2.111 0 0 1 2.76 1.143l7.295 17.61a2.112 2.112 0 0 1-1.143 2.76l-7.64 3.165c-.256.106-.53.16-.806.161Zm.343-23.864a.973.973 0 0 0-.373.075l-7.641 3.165a.975.975 0 0 0-.527 1.273l7.295 17.612a.976.976 0 0 0 1.273.528l7.641-3.167a.975.975 0 0 0 .527-1.274l-7.295-17.61a.976.976 0 0 0-.9-.602Z"
          fill="#5A78FF"
        />
        <path
          style="mix-blend-mode: multiply"
          opacity=".7"
          d="m30.186 10.465-1.051.435 2.888 6.973 1.05-.435-2.887-6.973Z"
          fill="url(#a)"
        />
        <path
          style="mix-blend-mode: multiply"
          opacity=".7"
          d="m35.463 26.179 1.05-.435-3.27-7.893-1.05.435 3.27 7.893Z"
          fill="url(#b)"
        />
        <path
          d="M34.136 20.947c-2.185.906-4.01 2.155-5.204 3.66l-3.08-7.438a13.006 13.006 0 0 1 5.204-3.661c2.186-.906 4.393-1.345 6.269-1.09l3.08 7.44a13.002 13.002 0 0 0-6.269 1.089Z"
          fill="#5A78FF"
        />
        <g style="mix-blend-mode: multiply" opacity=".4">
          <path
            opacity=".9"
            d="M34.136 20.947c-2.185.906-4.01 2.155-5.204 3.66l-3.08-7.438a13.006 13.006 0 0 1 5.204-3.661c2.186-.906 4.393-1.345 6.269-1.09l3.08 7.44a13.002 13.002 0 0 0-6.269 1.089Z"
            fill="url(#c)"
          />
          <path
            opacity=".7"
            d="M34.136 20.947c-2.185.906-4.01 2.155-5.204 3.66l-3.08-7.438a13.006 13.006 0 0 1 5.204-3.661c2.186-.906 4.393-1.345 6.269-1.09l3.08 7.44a13.002 13.002 0 0 0-6.269 1.089Z"
            fill="url(#d)"
          />
        </g>
        <path
          d="M35.14 23.215c-2.365 0-4.53.457-6.209 1.39v-7.522a13.007 13.007 0 0 1 6.21-1.39c2.364 0 4.572.439 6.207 1.392v7.521a13.002 13.002 0 0 0-6.208-1.391Z"
          fill="#5A78FF"
        />
        <defs>
          <linearGradient
            id="a"
            x1="31.842"
            y1="15.988"
            x2="31.371"
            y2="10.486"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#504678" />
            <stop offset=".302" stop-color="#504678" stop-opacity=".616" />
            <stop offset=".608" stop-color="#504678" stop-opacity=".283" />
            <stop offset=".852" stop-color="#504678" stop-opacity=".076" />
            <stop offset="1" stop-color="#504678" stop-opacity="0" />
          </linearGradient>
          <linearGradient
            id="b"
            x1="33.517"
            y1="19.957"
            x2="33.812"
            y2="26.122"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#504678" />
            <stop offset=".302" stop-color="#504678" stop-opacity=".616" />
            <stop offset=".608" stop-color="#504678" stop-opacity=".283" />
            <stop offset=".852" stop-color="#504678" stop-opacity=".076" />
            <stop offset="1" stop-color="#504678" stop-opacity="0" />
          </linearGradient>
          <linearGradient
            id="c"
            x1="27.846"
            y1="20.481"
            x2="33.618"
            y2="16.546"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#504678" />
            <stop offset=".179" stop-color="#504678" stop-opacity=".872" />
            <stop offset=".526" stop-color="#504678" stop-opacity=".536" />
            <stop offset="1" stop-color="#504678" stop-opacity="0" />
          </linearGradient>
          <linearGradient
            id="d"
            x1="32.798"
            y1="16.634"
            x2="32.542"
            y2="14.587"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#504678" />
            <stop offset=".643" stop-color="#504678" stop-opacity=".332" />
            <stop offset="1" stop-color="#504678" stop-opacity="0" />
          </linearGradient>
        </defs>
      </svg>
      <svg
        preserveAspectRatio="xMidYMid meet"
        viewBox="0 0 60 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="Visa"
      > 
        <rect x=".5" y=".5" width="59" height="39" rx="3.5" fill="#fff" />
        <rect x=".5" y=".5" width="59" height="39" rx="3.5" stroke="#E7E7E4" />
        <path
          d="M26.632 27.255h-3.857l2.412-14.001h3.857l-2.412 14ZM40.613 13.596A10.052 10.052 0 0 0 37.154 13c-3.809 0-6.491 1.906-6.507 4.632-.032 2.01 1.92 3.128 3.38 3.798 1.492.685 2 1.133 2 1.743-.016.938-1.206 1.37-2.317 1.37-1.54 0-2.365-.222-3.619-.744l-.508-.224-.54 3.143c.905.387 2.572.73 4.302.745 4.047 0 6.682-1.877 6.713-4.781.015-1.594-1.015-2.815-3.237-3.813-1.35-.64-2.176-1.073-2.176-1.728.016-.596.7-1.206 2.222-1.206a6.92 6.92 0 0 1 2.873.536l.349.148.524-3.023Z"
          fill="#193AC3"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M47.517 13.254H50.5l3.112 14H50.04s-.35-1.608-.46-2.1h-4.952l-.81 2.1h-4.047l5.73-12.839c.396-.908 1.095-1.161 2.015-1.161Zm-.238 5.123s-1.222 3.113-1.54 3.918h3.206c-.158-.7-.889-4.052-.889-4.052l-.27-1.206c-.113.31-.277.738-.388 1.026-.075.195-.125.326-.119.314Z"
          fill="#193AC3"
        />
        <path
          d="M6.063 13.254h6.206c.841.03 1.524.283 1.745 1.177l1.35 6.433.412 1.937 3.777-9.547h4.079L17.569 27.24h-4.078l-3.438-12.166C8.867 14.424 7.513 13.9 6 13.537l.063-.283Z"
          fill="#193AC3"
        />
      </svg>
      <svg
        preserveAspectRatio="xMidYMid meet"
        viewBox="0 0 60 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="Dankort"
      > 
        <rect x=".5" y=".5" width="59" height="39" rx="3.5" fill="#fff" />
        <rect x=".5" y=".5" width="59" height="39" rx="3.5" stroke="#E7E7E4" />
        <path
          d="M21.512 7.25H38.193c6.77 0 12.26 5.51 12.26 12.308 0 6.8-5.49 12.308-12.26 12.308H21.51c-6.77 0-12.261-5.508-12.261-12.308 0-6.798 5.49-12.308 12.262-12.308Z"
          fill="#fff"
          stroke="#C8C8C8"
          stroke-width=".5"
        />
        <path
          d="m41.109 18.798 5.547 6.622a10.295 10.295 0 0 0 1.823-5.862c0-2.297-.752-4.421-2.02-6.138l-5.35 5.378ZM25.39 12.861c4.143 0 7.264 1.058 7.585 4.561l4.36-4.562h8.68a10.252 10.252 0 0 0-7.822-3.627H21.512c-3.13 0-5.936 1.409-7.824 3.628H25.39ZM18.566 17.277l-1.48 4.083h5.969c1.764 0 2.297-.733 2.692-2.145.391-1.396-.59-1.938-1.796-1.938h-5.385ZM37.37 25.977l-4.61-5.849c-.852 3.988-3.643 5.848-8.875 5.848H13.46c1.887 2.38 4.795 3.908 8.053 3.908h16.68c3.258 0 6.165-1.528 8.051-3.907h-8.872Z"
          fill="#E2231A"
        />
      </svg>
      <svg
        preserveAspectRatio="xMidYMid meet"
        viewBox="0 0 60 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="Mastercard"
      > 
        <rect x=".5" y=".5" width="59" height="39" rx="3.5" fill="#fff" />
        <rect x=".5" y=".5" width="59" height="39" rx="3.5" stroke="#E7E7E4" />
        <path d="M35.281 28.977H24.943V10.512H35.28v18.465Z" fill="#FF5F00" />
        <path
          d="M25.605 19.743c0-3.746 1.765-7.082 4.513-9.232A11.812 11.812 0 0 0 22.815 8C16.29 8 11 13.257 11 19.743c0 6.485 5.29 11.743 11.815 11.743 2.757 0 5.293-.939 7.303-2.511-2.748-2.15-4.513-5.487-4.513-9.232Z"
          fill="#EB001B"
        />
        <path
          d="M49.225 19.743c0 6.485-5.29 11.743-11.815 11.743-2.757 0-5.293-.939-7.303-2.511 2.749-2.15 4.513-5.487 4.513-9.232 0-3.746-1.764-7.082-4.513-9.232A11.816 11.816 0 0 1 37.41 8c6.526 0 11.815 5.257 11.815 11.743Z"
          fill="#F79E1B"
        />
      </svg>
    </div>

    <template v-if="miniBasket">
      <p class="kb-basket-aside__help-text">
        <span>
          {{ getLabel("miniBasketHelpTextTop") }}
        </span>
        <span>
          {{ getLabel("miniBasketHelpTextTimes") }}
        </span>
      </p>
    </template>
  </aside>
</template>
